// src/components/minigames/MusicPlayerV2.jsx
// NeuraCinema KARAOKE ENGINE v6 · PREMIUM EDITION · Silicon Valley Grade

import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { alternarPantallaCompleta, hayPantallaCompleta } from './pantallaCompleta';
import { useYouTubePlayer } from './useYouTubePlayer';

/* ─── Constants ─────────────────────────────────────────── */
const MAX_LIVES = 5;
const BASE_POINTS = 100;

const CHOICE_PALETTE = [
    { bg: 'linear-gradient(135deg,#c0392b,#e74c3c)', shadow: 'rgba(231,76,60,0.55)' },
    { bg: 'linear-gradient(135deg,#16a085,#1abc9c)', shadow: 'rgba(26,188,156,0.55)' },
    { bg: 'linear-gradient(135deg,#d35400,#e67e22)', shadow: 'rgba(230,126,34,0.55)' },
    { bg: 'linear-gradient(135deg,#1a5276,#2980b9)', shadow: 'rgba(41,128,185,0.55)' },
];

/* ─── Partículas ─────────────────────────────────────────── */
function Particles({ trigger, correct }) {
    const [items, setItems] = useState([]);
    useEffect(() => {
        if (!trigger) return;
        const n = correct ? 32 : 14;
        const colors = correct
            ? ['#34d399', '#6ee7b7', '#fbbf24', '#f9a8d4', '#a78bfa', '#38bdf8']
            : ['#f87171', '#fca5a5', '#fb923c'];
        setItems(Array.from({ length: n }, (_, i) => ({
            id: Date.now() + i,
            x: 30 + Math.random() * 40,
            y: 25 + Math.random() * 35,
            angle: (i / n) * 360 + Math.random() * 20,
            dist: 60 + Math.random() * 110,
            color: colors[i % colors.length],
            size: 4 + Math.random() * 7,
            dur: 0.6 + Math.random() * 0.45,
            shape: Math.random() > 0.5 ? '50%' : '2px',
        })));
        setTimeout(() => setItems([]), 1200);
    }, [trigger]);
    if (!items.length) return null;
    return (
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 200, overflow: 'hidden' }}>
            {items.map(p => (
                <div key={p.id} style={{
                    position: 'absolute', left: `${p.x}%`, top: `${p.y}%`,
                    width: p.size, height: p.size, borderRadius: p.shape,
                    background: p.color, boxShadow: `0 0 ${p.size * 2.5}px ${p.color}`,
                    transform: 'translate(-50%,-50%)',
                    animation: `kp-particle ${p.dur}s cubic-bezier(0,.9,.57,1) forwards`,
                    '--dx': `${Math.cos(p.angle * Math.PI / 180) * p.dist}px`,
                    '--dy': `${Math.sin(p.angle * Math.PI / 180) * p.dist}px`,
                }} />
            ))}
        </div>
    );
}

/* ─── Confeti ──────────────────────────────────── */
//  Rob, 14-10-2026: «cuando llega el alumno con sus 5 vidas al final de la
//  canción, lanzar confeti».
//
//  Cae de arriba y se queda un rato, al revés que las partículas de acertar —que
//  salen disparadas y se van en un segundo—. Es la diferencia entre «bien» y
//  «la has hecho entera sin fallar ni una».
function Confeti({ encendido, aLoGrande = false }) {
    const [trozos, setTrozos] = useState([]);
    useEffect(() => {
        if (!encendido) { setTrozos([]); return; }
        //  Llegar al final ya se celebra; hacerlo sin fallar ni una, el doble.
        //  Lo que cambia con las vidas es el TAMAÑO de la fiesta, no si la hay.
        const colores = aLoGrande
            ? ['#fbbf24', '#f59e0b', '#fde68a', '#34d399', '#f9a8d4', '#a78bfa']
            : ['#a78bfa', '#38bdf8', '#6ee7b7', '#f9a8d4'];
        setTrozos(Array.from({ length: aLoGrande ? 110 : 55 }, (_, i) => ({
            id: i,
            x: Math.random() * 100,
            ancho: 5 + Math.random() * 6,
            alto: 8 + Math.random() * 10,
            color: colores[i % colores.length],
            giro: Math.random() * 360,
            dur: 2.4 + Math.random() * 2.2,
            espera: Math.random() * 1.8,
        })));
    }, [encendido, aLoGrande]);
    if (!trozos.length) return null;
    return (
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none',
                      zIndex: 320, overflow: 'hidden' }}>
            {trozos.map(t => (
                <div key={t.id} style={{
                    position: 'absolute', left: `${t.x}%`, top: '-6%',
                    width: t.ancho, height: t.alto, background: t.color,
                    borderRadius: 2, opacity: 0.9,
                    animation: `kp-confeti ${t.dur}s linear ${t.espera}s infinite`,
                    '--giro': `${t.giro}deg`,
                }} />
            ))}
        </div>
    );
}

/* ─── Score Popup (floating +pts) ───────────────────────── */
function ScorePopup({ points, trigger }) {
    const [visible, setVisible] = useState(false);
    const [key, setKey] = useState(0);
    useEffect(() => {
        if (!trigger || !points) return;
        setKey(k => k + 1);
        setVisible(true);
        setTimeout(() => setVisible(false), 900);
    }, [trigger]);
    if (!visible || !points) return null;
    return (
        <div key={key} style={{
            position: 'absolute', top: '18%', right: '12%', zIndex: 210, pointerEvents: 'none',
            fontSize: 'clamp(18px,4vw,28px)', fontWeight: 900,
            fontFamily: "'Inter', sans-serif",
            color: '#fbbf24',
            textShadow: '0 0 20px rgba(251,191,36,0.9), 0 2px 8px rgba(0,0,0,0.8)',
            animation: 'kp-score-pop 0.9s cubic-bezier(0,.9,.57,1) forwards',
        }}>+{points}</div>
    );
}

/* ─── Combo Ring ─────────────────────────────────────────── */
//  Aquí estuvo un anillo con una cuenta atrás de cinco segundos que daba puntos
//  por responder rápido. Rob lo quitó el 14-10-2026:
//
//      «tenemos un contador, lo cual me genera estrés en vez de concentrarme;
//       aparte no entra al momento indicado y lo peor es que no se registran los
//       puntos. Lo ideal es eliminar esa base de estrés».
//
//  Y es peor que molesto: «por seleccionar al azar con el contador de 5 s vi que
//  metí palabras que no son». Un cronómetro en una pantalla donde hay que OÍR,
//  LEER la letra y ELEGIR entre cuatro opciones no mide reflejos: fuerza a pulsar
//  cualquier cosa. Y entrenar el oído no es un concurso de velocidad.
//
//  Con él se fue el bono por rapidez. Los puntos salen de acertar, que es lo
//  único que de verdad quiere medir esto.

/* ─── Lives Display ─────────────────────────────────────── */
function LivesDisplay({ lives, max }) {
    return (
        <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
            {Array.from({ length: max }).map((_, i) => (
                <div key={i} style={{
                    width: i < lives ? 10 : 8,
                    height: i < lives ? 10 : 8,
                    borderRadius: '50%',
                    background: i < lives
                        ? 'radial-gradient(circle at 35% 35%, #ff6b8a, #e84393)'
                        : 'rgba(255,255,255,0.1)',
                    boxShadow: i < lives ? '0 0 8px rgba(232,67,147,0.8), 0 0 16px rgba(232,67,147,0.3)' : 'none',
                    transition: 'all 0.4s cubic-bezier(0.34,1.56,0.64,1)',
                    transform: i < lives ? 'scale(1)' : 'scale(0.75)',
                }} />
            ))}
        </div>
    );
}

/* ─── Flame Stat ─────────────────────────────────────────── */
function FlameStat({ flame }) {
    const col = flame > 70 ? '#f97316' : flame > 40 ? '#fb923c' : '#64748b';
    const glow = flame > 70 ? 'rgba(249,115,22,0.6)' : flame > 40 ? 'rgba(251,146,60,0.4)' : 'transparent';
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', letterSpacing: 1.5, fontFamily: 'monospace', textTransform: 'uppercase' }}>Heat</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{
                    width: 72, height: 5, background: 'rgba(255,255,255,0.06)',
                    borderRadius: 3, overflow: 'hidden',
                }}>
                    <div style={{
                        height: '100%', width: `${flame}%`, borderRadius: 3,
                        background: `linear-gradient(90deg, #fb923c, ${col})`,
                        boxShadow: `0 0 8px ${glow}`,
                        transition: 'width .5s ease, background .4s',
                    }} />
                </div>
                <span style={{
                    fontSize: 11, fontWeight: 800, color: col, fontFamily: "'Inter', monospace",
                    textShadow: `0 0 8px ${glow}`, minWidth: 28,
                }}>{flame}%</span>
            </div>
        </div>
    );
}

/* ─── Score Display ──────────────────────────────────────── */
function ScoreDisplay({ score, scoreKey }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', letterSpacing: 1.5, fontFamily: 'monospace', textTransform: 'uppercase' }}>Score</div>
            <div key={scoreKey} style={{
                fontSize: 'clamp(20px,5vw,28px)', fontWeight: 900,
                fontFamily: "'Inter', monospace",
                color: '#c4b5fd',
                textShadow: '0 0 16px rgba(196,181,253,0.7), 0 0 32px rgba(139,92,246,0.3)',
                lineHeight: 1,
                animation: 'kp-score-bump 0.35s cubic-bezier(0.34,1.56,0.64,1)',
            }}>{score.toLocaleString()}</div>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
export default function MusicPlayerV2({ token, song, onFinish, onBack }) {
    const lines = song.built_lines || [];

    /* ── State ─────────────────────────────────────────── */
    // ── EL CAJON DE RESPUESTAS SE MIDE, NO SE ADIVINA ───────────────────────
    // El area de la letra reservaba 190px fijos para dejarle sitio al cajon que
    // sube desde abajo. Ese numero se calculo mirando una pantalla de
    // escritorio: en un telefono el cajon lleva el temporizador, el rotulo y
    // DOS FILAS de botones, y pasa holgadamente de 190 — asi que tapaba la
    // linea de la letra justo cuando el alumno la necesita para responder.
    //
    // Un ResizeObserver da el alto real. Cuesta unas lineas y elimina el
    // problema para cualquier pantalla, incluidas las que no existen todavia.
    const cajonRef = useRef(null);
    const [altoCajon, setAltoCajon] = useState(190);

    // ── EL TAMAÑO DE LA LETRA SE MIDE, NO SE CALCULA ────────────────────────
    //
    // Antes era `clamp(18px, min(5.5vw, 10cqh), 28px)`. El termino que mandaba
    // siempre era `5.5vw` —el ANCHO de la pantalla— cuando lo que falta es
    // ALTO. Por eso una frase larga se salia igual: el navegador la partia en
    // cinco filas y nadie bajaba el tamaño.
    //
    // Y no se puede arreglar con mas CSS: cuantas filas ocupa una frase depende
    // de donde caen las palabras, y eso no existe como numero dentro de un
    // `clamp`. La unica forma honesta es dibujarla, mirar si cabe, y si no,
    // probar mas pequeño. Es lo que hace `useLayoutEffect`: corre ANTES de que
    // el navegador pinte, asi que el alumno nunca ve el salto.
    const zonaRef = useRef(null);
    const activaRef = useRef(null);
    const [fsLetra, setFsLetra] = useState(null);   // null = todavia sin medir
    const [topeLetra, setTopeLetra] = useState(null);
    const [sitioJusto, setSitioJusto] = useState(false);
    const [tickZona, setTickZona] = useState(0);

    const [lineIdx, setLineIdx] = useState(0);
    const [paused, setPaused] = useState(false);
    const [isLineActive, setIsLineActive] = useState(false);
    const isLineActiveRef = useRef(false);
    const [answers, setAnswers] = useState({});
    const [revealed, setRevealed] = useState(false);
    const [activeBlank, setActiveBlank] = useState(0);
    const [lives, setLives] = useState(MAX_LIVES);
    const [flame, setFlame] = useState(50);
    const [score, setScore] = useState(0);
    const [scoreKey, setScoreKey] = useState(0);
    const [lastPoints, setLastPoints] = useState(0);
    const [pointsTrigger, setPointsTrigger] = useState(0);
    const [correctCount, setCorrectCount] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [streak, setStreak] = useState(0);
    const [effect, setEffect] = useState(null);
    const [partTrigger, setPartTrigger] = useState(0);
    const [partCorrect, setPartCorrect] = useState(true);
    const [isPlaying, setIsPlaying] = useState(false);

    const lineWordBank = useMemo(() => {
        const line = lines[lineIdx];
        if (!line || !line.blanks || !line.blanks.length) return [];

        const corrects = line.blanks.map(b => b.correct);
        const distractors = [];

        line.blanks.forEach(b => {
            (b.choices || []).forEach(c => {
                if (!corrects.includes(c) && !distractors.includes(c)) {
                    distractors.push(c);
                }
            });
        });

        const bankStr = [...corrects];
        let dIdx = 0;
        const targetSize = Math.max(4, corrects.length);

        while (bankStr.length < targetSize && dIdx < distractors.length) {
            bankStr.push(distractors[dIdx++]);
        }

        bankStr.sort(() => Math.random() - 0.5);
        return bankStr.map((word, id) => ({ id: `wb-${id}`, word }));
    }, [lineIdx, lines]);

    /* ── Refs ──────────────────────────────────────────── */
    const { playerRef, createPlayer, destroyPlayer } = useYouTubePlayer();
    const pollRef = useRef(null);
    const pauseRequested = useRef(false);

    //  ── OÍR EL VERSO OTRA VEZ, UNA SOLA (14-10-2026) ──────────────────
    //  Rob: «es volver a escuchar y resolver, porque lo estoy escuchando y me
    //  costó trabajo seleccionar: mi vista se fue a el oído, la letra y las
    //  opciones».
    //
    //  Tres cosas a la vez en dos segundos de canción. Al básico lo decepciona y
    //  al avanzado lo frustra justo cuando está descifrando la frase. Es el
    //  mismo botón que en los vídeos, y por el mismo motivo: una segunda
    //  oportunidad de ESCUCHAR, no de adivinar.
    //
    //  Una por verso. Sin el límite deja de ser una segunda oportunidad y pasa a
    //  ser un bucle hasta que suene la palabra que falta, que no es escuchar.
    const [repetidos, setRepetidos] = useState(() => new Set());
    //  Qué verso se está repitiendo y hasta dónde. Es un `ref` porque lo lee el
    //  reloj, que corre cada 100 ms fuera del ciclo de React.
    const repiteRef = useRef(null);

    //  ── LA VUELTA DE HONOR (14-10-2026) ───────────────────────────────
    //  Rob: «cuando llega el alumno con sus 5 vidas al final de la canción,
    //  lanzar confeti y darle al usuario ver el vídeo completo de la canción,
    //  donde el protagonista es el vídeo y la letra en formato normal, y disfrute
    //  llegar al final poniendo en efecto la palabra que seleccionó. Sólo si ahí
    //  lo decide el alumno».
    //
    //  Lo último es lo importante: se OFRECE. Un vídeo de siete minutos que
    //  arranca solo después de ganar no es un premio, es un peaje.
    const [celebrando, setCelebrando] = useState(null);   // { correct, total }
    const [disfrutando, setDisfrutando] = useState(false);
    const disfrutaRef = useRef(false);
    //  Lo que eligió en cada verso, para poder enseñarlo en la vuelta de honor:
    //  `answers` se vacía al pasar de verso, así que sin esto la letra saldría
    //  con los huecos otra vez en blanco —justo lo contrario del premio—.
    const [loQueEligio, setLoQueEligio] = useState({});
    const lineIdxRef = useRef(lineIdx);
    const linesRef = useRef(lines);
    const correctCountRef = useRef(correctCount);
    const totalCountRef = useRef(totalCount);

    useEffect(() => { lineIdxRef.current = lineIdx; }, [lineIdx]);
    useEffect(() => { linesRef.current = lines; }, [lines]);
    useEffect(() => { correctCountRef.current = correctCount; }, [correctCount]);
    useEffect(() => { totalCountRef.current = totalCount; }, [totalCount]);

    /* ── Init player ────────────────────────────────────── */
    useEffect(() => {
        createPlayer('kp2-yt-player', song.youtube_id, () => { startPoll(); });
        return () => {
            destroyPlayer();
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, []);

    const startPoll = useCallback(() => {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(() => {
            if (!playerRef.current || pauseRequested.current) return;
            try {
                const state = playerRef.current.getPlayerState();
                setIsPlaying(state === 1 || state === 3);
                if (state !== 1) return;
                const t = playerRef.current.getCurrentTime();
                const idx = lineIdxRef.current;
                const curLines = linesRef.current;
                const line = curLines[idx];
                if (!line) return;

                //  ── LA VUELTA DE HONOR ───────────────────────────────────
                //  Aquí el vídeo NO se para nunca: ya se ganó. Sólo se va pasando
                //  de verso para que la letra siga a lo que suena.
                if (disfrutaRef.current) {
                    const siguiente = curLines[idx + 1];
                    if (siguiente && siguiente.time != null && t >= siguiente.time) {
                        lineIdxRef.current = idx + 1;
                        setLineIdx(idx + 1);
                    }
                    return;
                }

                const blanks = line.blanks || [];

                if (blanks.length === 0) {
                    // MODO KARAOKE (sin blancos)
                    const nextLine = curLines[idx + 1];
                    if (nextLine && nextLine.time != null && t >= nextLine.time) {
                        const nextIdx = idx + 1;
                        lineIdxRef.current = nextIdx;
                        setLineIdx(nextIdx);
                    } else if (!nextLine && line.time != null && t >= line.time + 10) {
                        // Última línea, terminar el juego después de un tiempo prudente
                        finishGame(correctCountRef.current, totalCountRef.current);
                    }
                    return;
                }

                // Mostrar el cajón interactivo 1.5s antes de que empiece la línea
                const shouldBeActive = line.time != null && t >= (line.time - 1.5);
                if (shouldBeActive !== isLineActiveRef.current) {
                    isLineActiveRef.current = shouldBeActive;
                    setIsLineActive(shouldBeActive);
                }

                // Pausar SOLO cuando termina el tiempo del segmento (pauseAt)
                const pauseAt = blanks[0].pauseAt;

                //  ── MIENTRAS SE REPITE EL VERSO ──────────────────────────
                //  El `seekTo` de YouTube NO es inmediato: durante un par de
                //  décimas `getCurrentTime()` sigue devolviendo el tiempo de
                //  ANTES —el del final del verso—, así que el primer latido
                //  volvería a pausar y la repetición se abortaría sin haber
                //  empezado. Costó encontrarlo en los vídeos; aquí se pone
                //  bien desde el principio.
                if (repiteRef.current) {
                    const r = repiteRef.current;
                    if (!r.llego) {
                        if (t < r.hasta - 0.5) { r.llego = true; return; }
                        //  Y si el salto se pierde, se deja de esperar: mejor
                        //  volver a la pregunta que quedarse sonando sin fin.
                        if (Date.now() - r.pedido > 4000) repiteRef.current = null;
                        else return;
                    } else if (t < r.hasta) {
                        return;
                    } else {
                        repiteRef.current = null;
                    }
                }

                if (pauseAt != null && t >= pauseAt) {
                    playerRef.current.pauseVideo();
                    pauseRequested.current = true;
                    setPaused(true);
                    setIsPlaying(false);
                }
            } catch (_) { }
        }, 100);
    }, [playerRef]);

    useEffect(() => {
        if (playerRef.current) startPoll();
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, [lineIdx, startPoll]);

    /* ── Current line ───────────────────────────────────── */
    const line = lines[lineIdx] || { words: [], blanks: [] };
    const blanks = line.blanks || [];
    const allAnswered = blanks.every((_, i) => answers[i] !== undefined);

    //  ── EL HUECO QUE SE ESTÁ RESPONDIENDO (14-10-2026) ───────────────
    //  Rob: «si llegamos a esta parte y sólo entendí la parte 3, que es la
    //  correcta, lo ideal es darle clic a su casilla y seleccionar la respuesta,
    //  sin necesidad de poner la primera respuesta».
    //
    //  Antes esto era siempre EL PRIMERO SIN RESPONDER, pasara lo que pasara, y
    //  las respuestas se escribían en `activeBlank`. O sea que eran dos cosas
    //  distintas diciendo cuál es el hueco de turno, y por eso no se podía
    //  empezar por el de en medio: la pantalla marcaba uno y la respuesta caía
    //  en otro.
    //
    //  Ahora manda el elegido mientras siga vacío, y si no, el primero libre.
    const currentBlank = (() => {
        //  En la vuelta de honor no hay hueco de turno: ya se respondió todo. Con
        //  esto se caen de golpe el cajón de opciones y el hueco de abajo que le
        //  hace sitio, sin tener que acordarse de apagarlos uno a uno.
        if (revealed || disfrutando) return null;
        const elegido = (blanks[activeBlank] && answers[activeBlank] === undefined)
            ? activeBlank
            : blanks.findIndex((_, i) => answers[i] === undefined);
        return blanks[elegido] ? { ...blanks[elegido], idx: elegido } : null;
    })();

    /* ── Oír el verso otra vez ──────────────────────── */
    //  Vuelve al principio del verso —con el segundo y medio de carrerilla que
    //  ya usa la pantalla para sacar el cajón— y lo deja sonar hasta el mismo
    //  punto en el que se paró. Ni uno más: oír de más sería oír lo que todavía
    //  no toca.
    //
    //  No cuesta nada. Castigar al que está intentando entender es enseñarle a no
    //  intentarlo: que lo repita es justo lo que se quiere que haga.
    const puedeRepetir = paused && !revealed && !repetidos.has(lineIdx)
        && line?.time != null && blanks[0]?.pauseAt != null
        && blanks[0].pauseAt > line.time;

    const repiteElVerso = () => {
        if (!puedeRepetir || !playerRef.current) return;
        const desde = Math.max(0, Number(line.time) - 1.5);
        const hasta = Number(blanks[0].pauseAt);

        setRepetidos(prev => {
            const fuera = new Set(prev);
            fuera.add(lineIdx);
            return fuera;
        });
        repiteRef.current = { hasta, llego: false, pedido: Date.now() };
        //  El reloj está cortocircuitado mientras hay una pausa pedida: hay que
        //  soltarlo para que vuelva a mirar. Lo volverá a parar él solo en
        //  `pauseAt`, por el camino de siempre.
        pauseRequested.current = false;
        setPaused(false);
        setIsPlaying(true);
        try {
            playerRef.current.seekTo(desde, true);
            playerRef.current.playVideo();
        } catch (_) { }
    };

    /* ── Choose option ──────────────────────────────────── */
    const handleChoose = (choiceObj) => {
        if (revealed) return;
        //  Donde cae la palabra es el hueco MARCADO, que es el que el alumno ve
        //  encendido. Dicho claro para que conste: con la vuelta de arriba que
        //  hay unas líneas más abajo, `activeBlank` y `currentBlank.idx` acaban
        //  siendo siempre el mismo número, así que CAMBIAR UNO POR OTRO NO SE
        //  NOTA en ninguna prueba —se intentó—.
        //
        //  Son dos cinturones para lo mismo y se quedan los dos a propósito:
        //  éste porque la respuesta tiene que ir donde se está viendo el cursor
        //  pase lo que pase con el estado, y el otro porque el estado no debe
        //  mentir. Lo que no se puede es quitar los dos.
        const destino = currentBlank?.idx;
        if (destino === undefined || destino < 0) return;

        //  La cuenta se hace FUERA del `setState`: React llama al actualizador
        //  dos veces en desarrollo y no lo corre en el momento, así que meter
        //  aquí el `setActiveBlank` era pedirle que adivinara.
        const siguientes = { ...answers, [destino]: choiceObj };
        //  El siguiente vacío: primero hacia abajo y, si no queda ninguno, se
        //  vuelve a mirar desde arriba, porque ahora se puede haber empezado por
        //  el de en medio y quedar uno suelto detrás.
        //
        //  La vuelta de arriba es un cinturón, y conviene decirlo: `currentBlank`
        //  ya cae en el primero libre cuando `activeBlank` apunta a uno
        //  respondido, así que quitarla no cambiaría lo que ve el alumno. Se
        //  queda para que el estado diga la verdad y no dependa de ese rescate.
        let siguiente = blanks.findIndex((_, i) => i > destino && siguientes[i] === undefined);
        if (siguiente === -1) siguiente = blanks.findIndex((_, i) => siguientes[i] === undefined);

        setAnswers(siguientes);
        if (siguiente !== -1) setActiveBlank(siguiente);
    };

    /* ── Quitar una palabra ya puesta ──────────────── */
    //  Rob, 14-10-2026: «por seleccionar al azar con el contador de 5 s vi que
    //  metí palabras que no son; cuando volví a escuchar me di cuenta del error
    //  pero no pude revertir mi error. ¿Qué tal si al hacer clic a la palabra
    //  seleccionada sea como un regreso a las opciones?».
    //
    //  Es lo que hace que oír otra vez sirva de algo: volver a escuchar y
    //  enterarte de que te equivocaste sin poder cambiarlo es peor que no haberlo
    //  oído. La palabra vuelve sola al banco —las opciones se marcan como usadas
    //  mirando las respuestas, así que no hay nada que devolver— y ese hueco se
    //  queda activo, que es donde se está mirando.
    const quitaLaPalabra = (idx) => {
        if (revealed) return;
        setAnswers(prev => {
            if (prev[idx] === undefined) return prev;
            const siguiente = { ...prev };
            delete siguiente[idx];
            return siguiente;
        });
        setActiveBlank(idx);
    };

    /* ── Submit answers ─────────────────────────────────── */
    const submitAnswers = (currentAnswers) => {
        const cb = lines[lineIdx]?.blanks || [];
        if (!cb.length) return;

        let gained = 0, allOk = true;
        cb.forEach((b, i) => {
            if (currentAnswers[i]?.word === b.correct) gained++;
            else allOk = false;
        });

        const newCorrect = correctCount + gained;
        const newTotal = totalCount + cb.length;
        const newStreak = allOk ? streak + 1 : 0;
        //  Los puntos salen de acertar y de nada más. Había un bono por responder
        //  rápido que ni siquiera se registraba y que empujaba a pulsar antes de
        //  haber oído.
        const points = gained * BASE_POINTS;
        const newScore = score + points;
        const newFlame = Math.min(100, flame + (allOk ? 15 : -20));
        const newLives = allOk ? lives : lives - 1;

        setEffect(allOk ? 'hit' : 'miss');
        setPartTrigger(t => t + 1);
        setPartCorrect(allOk);
        setTimeout(() => setEffect(null), 900);

        if (points > 0) {
            setLastPoints(points);
            setPointsTrigger(t => t + 1);
        }

        //  Lo que eligió en este verso, guardado para la vuelta de honor.
        setLoQueEligio(prev => ({ ...prev, [lineIdx]: currentAnswers }));

        setCorrectCount(newCorrect);
        setTotalCount(newTotal);
        setStreak(newStreak);
        setScore(newScore);
        setScoreKey(k => k + 1);
        setFlame(Math.max(0, newFlame));
        setLives(Math.max(0, newLives));
        setRevealed(true);
        setPaused(false);
        setIsPlaying(false);

        if (newLives <= 0) {
            setTimeout(() => finishGame(newCorrect, newTotal), 1500);
            return;
        }

        setTimeout(() => {
            setRevealed(false);
            setPaused(false);
            setIsPlaying(true);
            setAnswers({});
            setActiveBlank(0);
            if (lineIdx + 1 >= lines.length) {
                //  ── SE ACABÓ LA CANCIÓN: PREMIACIÓN ─────────────────────
                //  Rob, 14-10-2026: «es como una premiación al terminar su
                //  canción y reproducirla de nuevo con sus honores».
                //
                //  La primera versión de esto sólo saltaba con las cinco vidas
                //  intactas, y por eso Rob no llegó a verla nunca: en una canción
                //  de veintinueve huecos, acabar sin fallar ni uno no le pasa casi
                //  a nadie. Un premio que no se ve no es un premio.
                //
                //  Se celebra por LLEGAR AL FINAL, que es lo que él dijo. Lo que
                //  cambia con las vidas es el tamaño de la fiesta, no si la hay.
                setCelebrando({
                    correct: newCorrect,
                    total: newTotal,
                    vidas: newLives,
                    puntos: newScore,
                    perfecto: newLives >= MAX_LIVES,
                });
                return;
            } else {
                const nextIdx = lineIdx + 1;
                lineIdxRef.current = nextIdx;
                pauseRequested.current = false;
                setLineIdx(nextIdx);
                setTimeout(() => { try { playerRef.current?.playVideo(); } catch (_) { } }, 80);
            }
        }, 1500);
    };

    const finishGame = (c, t) => {
        if (pollRef.current) clearInterval(pollRef.current);
        onFinish({
            title: `${song.title} — ${song.artist}`,
            mode: 'music',
            score: Math.round((c / Math.max(1, t)) * 100),
            correct: c,
            total: t,
        });
    };

    useEffect(() => {
        if (allAnswered && !revealed) {
            const captured = answers;
            const t = setTimeout(() => submitAnswers(captured), 250);
            return () => clearTimeout(t);
        }
    }, [allAnswered, revealed]);

    /* ── La vuelta de honor ─────────────────────── */
    const arrancaLaVuelta = () => {
        if (!playerRef.current) return;
        disfrutaRef.current = true;
        setDisfrutando(true);
        setRevealed(false);
        setPaused(false);
        //  La letra TIENE que verse: es la mitad del premio. Con esto en `false`
        //  salía la pantalla de espera —la llamita y los tres puntos— y Rob se
        //  quedó mirando un emoji durante toda la canción. El cajón de opciones
        //  no vuelve por esto: lo apaga `currentBlank`, que en la vuelta es null.
        setIsLineActive(true);
        isLineActiveRef.current = true;
        setAnswers({});
        lineIdxRef.current = 0;
        setLineIdx(0);
        pauseRequested.current = false;
        try {
            playerRef.current.seekTo(0, true);
            playerRef.current.playVideo();
        } catch (_) { }
    };

    const vaAlMarcador = () => {
        disfrutaRef.current = false;
        const r = celebrando || { correct: correctCount, total: totalCount };
        finishGame(r.correct, r.total);
    };

    /* ── renderLine ─────────────────────────────────────── */
    const renderLine = (lineData, isActive, deHonor = false) => {
        const words = lineData.words || (lineData.text || '').split(' ');
        const lb = lineData.blanks || [];
        const bMap = {};
        lb.forEach((b, i) => { bMap[b.wi] = { ...b, idx: i }; });

        return words.map((word, wi) => {
            const blank = bMap[wi];
            if (!blank || !isActive) {
                return <span key={wi} style={{ marginRight: 5 }}>{word}</span>;
            }

            //  En la vuelta de honor la palabra sale de lo que eligió en su
            //  momento —`answers` ya se vació— y se pinta en verde: la hizo
            //  entera sin fallar, así que todas son la buena.
            //  En la vuelta de honor siempre se pinta el verso que suena, que es
            //  `lineIdx`: por eso no hace falta pasarlo por el objeto de la línea.
            const chosen = deHonor
                ? loQueEligio[lineIdx]?.[blank.idx]
                : answers[blank.idx];
            const isHere = !deHonor && !revealed && blank.idx === (currentBlank?.idx ?? -1);
            const isCorrect = deHonor ? chosen !== undefined
                : revealed && chosen?.word === blank.correct;
            const isWrong = !deHonor && revealed && chosen !== undefined
                && chosen?.word !== blank.correct;

            //  Un hueco con palabra puesta y todavía sin corregir se puede pulsar
            //  para devolverla. Los demás no: uno vacío no tiene nada que quitar,
            //  y uno ya corregido no se toca.
            const sePuedeQuitar = !deHonor && !revealed && chosen !== undefined;
            //  Y uno vacío se pulsa para responderlo a ÉL. Rob: «sólo entendí
            //  la parte 3» — obligarle a rellenar la 1 y la 2 primero es
            //  obligarle a inventarse dos respuestas para poder dar la que sabe.
            const sePuedeElegir = !deHonor && !revealed && chosen === undefined;
            const pulsable = sePuedeQuitar || sePuedeElegir;

            return (
                <span key={wi} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 5 }}>
                    <span
                        className={`kp-hueco${sePuedeQuitar ? ' quitable' : ''}`
                            + `${sePuedeElegir && !isHere ? ' elegible' : ''}`}
                        data-prueba={sePuedeQuitar ? 'kp-quitable'
                            : sePuedeElegir ? 'kp-elegible' : undefined}
                        role={pulsable ? 'button' : undefined}
                        tabIndex={pulsable ? 0 : undefined}
                        title={sePuedeQuitar ? 'Click to put it back'
                            : sePuedeElegir ? 'Click to answer this one' : undefined}
                        onClick={pulsable ? () => (sePuedeQuitar
                            ? quitaLaPalabra(blank.idx)
                            : setActiveBlank(blank.idx)) : undefined}
                        onKeyDown={pulsable ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                if (sePuedeQuitar) quitaLaPalabra(blank.idx);
                                else setActiveBlank(blank.idx);
                            }
                        } : undefined}
                        style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        // Estrecho cuando el sitio aprieta: un hueco de 88px se
                        // lleva media fila el solo y obliga a partir la frase.
                        minWidth: sitioJusto ? 62 : 88,
                        padding: sitioJusto ? '4px 10px' : '4px 16px',
                        borderRadius: 100,
                        fontWeight: 800, fontSize: '0.82em',
                        fontFamily: "'Inter', sans-serif",
                        border: `1.5px solid ${isCorrect ? 'rgba(52,211,153,0.7)' :
                            isWrong ? 'rgba(248,113,113,0.6)' :
                                isHere ? 'rgba(6,182,212,0.75)' :
                                    chosen ? 'rgba(139,92,246,0.6)' :
                                        'rgba(255,255,255,0.15)'
                            }`,
                        background:
                            isCorrect ? 'rgba(52,211,153,0.12)' :
                                isWrong ? 'rgba(248,113,113,0.12)' :
                                    isHere ? 'rgba(6,182,212,0.1)' :
                                        chosen ? 'rgba(139,92,246,0.1)' :
                                            'rgba(255,255,255,0.04)',
                        color:
                            isCorrect ? '#34d399' :
                                isWrong ? '#f87171' :
                                    isHere ? '#22d3ee' :
                                        chosen ? '#c4b5fd' :
                                            'rgba(255,255,255,0.3)',
                        boxShadow:
                            isCorrect ? '0 0 18px rgba(52,211,153,0.4)' :
                                isWrong ? '0 0 18px rgba(248,113,113,0.35)' :
                                    isHere ? '0 0 20px rgba(6,182,212,0.4)' : 'none',
                        transition: 'all .2s',
                        animation: isHere && !chosen ? 'kp-blank-pulse 2s ease-in-out infinite' : 'none',
                        cursor: pulsable ? 'pointer' : 'default',
                    }}>
                        {chosen ? chosen.word : '· · ·'}
                    </span>
                    {isWrong && (
                        <span style={{ color: '#34d399', fontSize: '0.62em', fontWeight: 800 }}>
                            ({blank.correct})
                        </span>
                    )}
                </span>
            );
        });
    };

    /* ── Computed ───────────────────────────────────────── */
    const pct = totalCount > 0 ? Math.round((correctCount / totalCount) * 100) : 0;
    const progress = lineIdx / Math.max(1, lines.length - 1);
    const allOkRevealed = blanks.every((b, i) => answers[i]?.word === b.correct);

    // Vive en `pantallaCompleta.js`: este bloque estaba copiado en tres
    // ficheros y el `catch` vacio dejaba al alumno pulsando un boton que no
    // hacia nada ni decia por que.
    const toggleFullscreen = () => { alternarPantallaCompleta(); };

    const togglePlayPause = () => {
        if (!playerRef.current || isLineActive || paused) return;
        try {
            const state = playerRef.current.getPlayerState();
            if (state === 1 || state === 3) {
                playerRef.current.pauseVideo();
                setIsPlaying(false);
                pauseRequested.current = true;
            } else {
                playerRef.current.playVideo();
                setIsPlaying(true);
                pauseRequested.current = false;
            }
        } catch (_) {}
    };

    // El alto del cajon cambia con el ancho de pantalla, con el numero de
    // opciones y hasta con la longitud de las palabras. Observarlo es lo unico
    // que funciona en todos esos casos a la vez.
    useEffect(() => {
        const nodo = cajonRef.current;
        if (!nodo || typeof ResizeObserver === 'undefined') return;
        const ob = new ResizeObserver(([e]) => {
            const alto = Math.round(e.contentRect.height);
            if (alto > 0) setAltoCajon(alto);
        });
        ob.observe(nodo);
        return () => ob.disconnect();
    }, [currentBlank, isLineActive]);

    // Girar el movil, abrir la pantalla completa o cerrar el teclado cambian el
    // hueco disponible sin que cambie la cancion. Hay que volver a medir.
    useEffect(() => {
        const nodo = zonaRef.current;
        if (!nodo || typeof ResizeObserver === 'undefined') return;
        const ob = new ResizeObserver(() => setTickZona(t => t + 1));
        ob.observe(nodo);
        return () => ob.disconnect();
    }, [isLineActive]);

    /**
     * Baja el tamaño de la letra hasta que la estrofa cabe entera.
     *
     * Busqueda binaria y no "resta 1px y repite": con 7 pasos se acierta al
     * decimo de pixel entre 13 y 28, y son 7 lecturas de layout en vez de
     * quince. En un movil de gama baja eso se nota.
     *
     * El suelo son 13px. Por debajo la letra deja de leerse a distancia de
     * brazo y ya no es un karaoke; si ni a 13px cabe —una estrofa larguisima en
     * un iPhone SE con el cajon abierto— es preferible que se recorte por abajo
     * de forma limpia a dejarla ilegible.
     */
    useLayoutEffect(() => {
        const zona = zonaRef.current;
        const linea = activaRef.current;
        if (!zona || !linea || !isLineActive) return;

        const MIN = 13, MAX = 28;
        const cs = getComputedStyle(zona);
        const hueco = zona.clientHeight
            - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
        if (hueco <= 0) return;

        // 240px es el sitio a partir del cual las tres lineas caben con holgura.
        // Con un umbral mas bajo varias pantallas quedaban en empate exacto —la
        // letra cabia por 0px— y una frase una fila mas larga volvia a romperlo.
        // Cambiar esto reordena el layout, asi que el efecto vuelve a correr;
        // no hay bucle porque `hueco` no depende de las fantasma.
        const aprieta = hueco < 240;
        setSitioJusto(aprieta);

        const visibles = [...zona.children]
            .filter(e => getComputedStyle(e).display !== 'none');
        const separaciones = parseFloat(cs.rowGap || 0) * Math.max(0, visibles.length - 1);

        // Lo que ocupan las lineas fantasma no se toca: son 20px fijos cada una.
        const fantasmas = visibles
            .filter(e => e !== linea)
            .reduce((a, e) => a + e.getBoundingClientRect().height, 0);

        const paraLaLetra = hueco - separaciones - fantasmas;

        const anterior = linea.style.fontSize;
        // Los 2px de margen no son manias: sin ellos la busqueda aceptaba un
        // tamaño que cabia por medio pixel, el redondeo posterior lo subia, y la
        // frase acababa recortada por dos o tres pixeles. Un recorte pequeño se
        // ve igual de mal que uno grande.
        const cabe = (px) => {
            linea.style.fontSize = px + 'px';
            return linea.scrollHeight <= paraLaLetra - 2;
        };

        // El techo es el mismo que pedia el diseño original, para que en una
        // pantalla holgada nada cambie de aspecto.
        const techo = Math.min(MAX, Math.max(MIN, window.innerWidth * 0.055));

        let elegido;
        if (cabe(techo)) {
            elegido = techo;
        } else {
            let bajo = MIN, alto = techo;
            for (let i = 0; i < 7; i++) {
                const medio = (bajo + alto) / 2;
                if (cabe(medio)) bajo = medio; else alto = medio;
            }
            elegido = bajo;
        }

        linea.style.fontSize = anterior;
        // Hacia ABAJO. `Math.round` podia devolver un tamaño mayor que el que se
        // acaba de comprobar que cabe, y deshacer la busqueda entera.
        setFsLetra(Math.floor(elegido * 10) / 10);
        // El tope se pone en pixeles y no en `max-height: 100%`: el porcentaje
        // se resolvia contra una caja distinta de la que aqui se calcula y no
        // llegaba a capar nada. Este numero es exactamente el hueco que queda.
        setTopeLetra(Math.floor(paraLaLetra));
    }, [lineIdx, lines, isLineActive, currentBlank, altoCajon, tickZona,
        answers, revealed, sitioJusto]);


    return (
        <div className={`mp-root${isPlaying ? " mp-root-playing" : ""}`
            + `${disfrutando ? " kp-modo-disfrute" : ""}`} style={{
            display: 'flex', flexDirection: 'column',
            width: 'calc(100% - 40px)', margin: '20px',
            background: '#07070f',
            backgroundImage: 'radial-gradient(ellipse 80% 40% at 50% 0%, rgba(88,28,220,0.12) 0%, transparent 70%)',
            color: '#fff',
            fontFamily: "'Inter', 'Nunito', sans-serif",
            overflow: 'hidden', position: 'relative',
            boxSizing: 'border-box',
            border: '2px solid #8B5CF6',
            borderRadius: '22px',
            boxShadow: isPlaying ? 'none' : 'inset 0 0 20px rgba(139,92,246,0.4), 0 0 15px rgba(139,92,246,0.4)',
        }}>

            {/* ── Global Styles ── */}
            <style>{`
@keyframes mp-atmosphere {
                    0% { box-shadow: inset 0 0 20px rgba(139, 92, 246, 0.4), 0 0 15px rgba(139, 92, 246, 0.4); }
                    25% { box-shadow: inset 0 0 20px rgba(139, 92, 246, 0.4), 0 0 35px rgba(139, 92, 246, 0.7), 0 0 60px rgba(139, 92, 246, 0.3); }
                    50% { box-shadow: inset 0 0 20px rgba(139, 92, 246, 0.4), 0 0 20px rgba(139, 92, 246, 0.5); }
                    75% { box-shadow: inset 0 0 20px rgba(139, 92, 246, 0.4), 0 0 45px rgba(139, 92, 246, 0.8), 0 0 90px rgba(139, 92, 246, 0.4); }
                    100% { box-shadow: inset 0 0 20px rgba(139, 92, 246, 0.4), 0 0 15px rgba(139, 92, 246, 0.4); }
                }
                /* ── EL ALTO DEL REPRODUCTOR ──────────────────────────────
                   100vh mide la pantalla CON la barra del navegador oculta.
                   En el Safari del iPhone eso son ~100px mas de los que se ven,
                   y como el cajon de respuestas vive pegado a 'bottom: 0', el
                   cajon terminaba fuera de la pantalla. 100dvh mide lo que de
                   verdad se ve. Se declaran los dos: el navegador que no
                   entienda dvh se queda con la primera, que es lo que habia. */
                .mp-root {
                    height: calc(100vh - 40px);
                    height: calc(100dvh - 40px);
                }

                /* ── CUANDO EL CAJON DEJA POCO SITIO ──────────────────────
                   Esto empezo siendo '@container (max-height: ...)', que era lo
                   elegante. No funciona aqui: la consulta se resuelve contra la
                   caja del karaoke CON su relleno —322px— y no contra los 105
                   que quedan libres una vez descontado el hueco del cajon.
                   Chromium y WebKit coinciden, asi que no es un fallo de uno.

                   Por eso el umbral lo decide JavaScript, que es quien ya mide
                   el hueco de verdad, y aqui solo queda la pintura. Una sola
                   fuente de la verdad en vez de dos que se contradicen.

                   Y tampoco vale hacerlo con clases CSS aqui: estas cajas
                   llevan sus estilos EN LINEA, y un estilo en linea le gana a
                   cualquier regla de hoja de estilos. Una regla
                   '.kp-justo .kp-ghost { display: none }' se escribe, se lee
                   bien, y no hace absolutamente nada contra un
                   'style={{ display: 'flex' }}'. Por eso el estrechamiento va
                   en el propio 'style' de cada caja, mas abajo, leyendo el
                   estado 'sitioJusto'.

                   Lo que se hace cuando aprieta:
                     · Se quitan las lineas fantasma. Son decoracion —ayudan a
                       seguir la cancion— pero no son el juego.
                     · Se ataca el numero de FILAS, que es lo que de verdad
                       ocupa: menos margen a los lados y el hueco de respuesta
                       mas estrecho, para que quepan mas palabras por fila. */
                /* Aqui habia un '.kp-karaoke { justify-content: safe center }'
                   para que un contenido mas alto que su caja no se partiera por
                   arriba Y por abajo a la vez. Se quito por dos razones: la caja
                   lleva 'justifyContent' en linea y una regla de hoja de estilos
                   nunca le iba a ganar —o sea que no hacia nada—, y ademas ya no
                   hace falta: la medicion garantiza que el contenido cabe. */

                .kp-ring { width: clamp(46px, 9vh, 68px); height: clamp(46px, 9vh, 68px); }

                /* ── EL REPARTO DEL ALTO ──────────────────────────────────
                   En un iPhone SE el reproductor tiene 528px y el cajon de
                   respuestas se lleva 188. Si ademas el video se queda 136, a la
                   letra —que es el juego— le sobran 105, y una estrofa larga no
                   entra ni bajando la fuente al minimo legible.

                   Asi que en pantallas cortas el video cede. Se decide por el
                   alto de la PANTALLA y no por el hueco medido a proposito: si
                   dependiera del hueco, encoger el video agrandaria el hueco,
                   que volveria a agrandar el video, y el alumno veria el video
                   dando saltos cada vez que responde. */
                .mp-video { width: min(100%, calc(min(24vh, 200px) * 16 / 9)); }
                @media (max-height: 700px) {
                    .mp-video { width: min(100%, calc(16vh * 16 / 9)); }
                }

                /* ── LA VUELTA DE HONOR: EL VÍDEO MANDA ──────────────────────
                   Rob: «el vídeo debería verse más grande para disfrutarlo, como
                   en el juego de vídeos, y acompañando la letra de la canción».

                   Durante el juego el vídeo es pequeño a propósito: lo que
                   importa es la letra y las opciones, y el vídeo roba atención.
                   En la vuelta de honor es al revés —ya no hay nada que
                   responder—, así que crece y la letra pasa a acompañarlo. */
                .kp-modo-disfrute .mp-video {
                    width: min(100%, calc(min(58vh, 860px) * 16 / 9));
                    transition: width .5s cubic-bezier(0.4,0,0.2,1);
                }
                @media (max-height: 700px) {
                    .kp-modo-disfrute .mp-video { width: min(100%, calc(48vh * 16 / 9)); }
                }
                /* Y la letra ocupa lo justo: acompaña, no compite. */
                .kp-modo-disfrute .kp-karaoke { flex: 0 1 auto; padding-bottom: 64px; }

                .mp-root-playing {
                    animation: mp-atmosphere 4s infinite ease-in-out;
                }

                @keyframes kp-particle {
                    0%   { transform: translate(-50%,-50%) scale(1); opacity:1; }
                    100% { transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(0); opacity:0; }
                }
                @keyframes kp-blank-pulse {
                    0%,100% { box-shadow: 0 0 10px rgba(6,182,212,0.2); }
                    50%     { box-shadow: 0 0 28px rgba(6,182,212,0.7), 0 0 56px rgba(6,182,212,0.15); }
                }
                @keyframes kp-screen-flash {
                    0%,100% { opacity:0; }
                    25%     { opacity:1; }
                }
                @keyframes kp-score-bump {
                    0%   { transform: scale(1.4); }
                    100% { transform: scale(1); }
                }
                @keyframes kp-score-pop {
                    0%   { transform: translateY(0) scale(1); opacity:1; }
                    100% { transform: translateY(-60px) scale(0.8); opacity:0; }
                }
                @keyframes kp-feedback-in {
                    0%   { transform: translateX(-50%) scale(0.7) translateY(12px); opacity:0; }
                    60%  { transform: translateX(-50%) scale(1.05) translateY(-2px); opacity:1; }
                    100% { transform: translateX(-50%) scale(1) translateY(0); opacity:1; }
                }
                @keyframes kp-ring-pulse {
                    0%,100% { transform: scale(1); }
                    50%     { transform: scale(1.06); }
                }
                @keyframes kp-life-lost {
                    0%,100% { transform: scale(1); }
                    25%     { transform: scale(1.4) rotate(-15deg); }
                    50%     { transform: scale(0.8) rotate(10deg); }
                }
                @keyframes kp-shake {
                    0%,100% { transform: translateX(0); }
                    20%     { transform: translateX(-8px); }
                    40%     { transform: translateX(8px); }
                    60%     { transform: translateX(-5px); }
                    80%     { transform: translateX(5px); }
                }
                @keyframes kp-streak-in {
                    0%   { transform: scale(0) rotate(-15deg); opacity:0; }
                    70%  { transform: scale(1.15) rotate(3deg); opacity:1; }
                    100% { transform: scale(1) rotate(0deg); opacity:1; }
                }
                @keyframes kp-line-in {
                    0%   { opacity:0; transform: translateY(8px); }
                    100% { opacity:1; transform: translateY(0); }
                }
                @keyframes kp-bounce-dot {
                    0%, 80%, 100% { transform: scale(0); opacity: 0.3; }
                    40% { transform: scale(1); opacity: 1; }
                }

                .kp-bounce-dot {
                    width: 8px;
                    height: 8px;
                    background-color: #a78bfa;
                    border-radius: 50%;
                    animation: kp-bounce-dot 1.4s infinite ease-in-out both;
                    box-shadow: 0 0 10px rgba(167,139,250,0.6);
                }

                .kp-choice {
                    cursor: pointer;
                    border: none;
                    border-radius: 18px;
                    color: #fff;
                    font-weight: 800;
                    font-size: clamp(14px, 3.5vw, 17px);
                    font-family: 'Inter', sans-serif;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    text-align: center;
                    position: relative;
                    overflow: hidden;
                    transition: transform .14s cubic-bezier(0.34,1.56,0.64,1), filter .14s;
                    min-height: clamp(48px, 8vh, 62px);
                    padding: clamp(10px, 1.5vh, 14px) 12px;
                    letter-spacing: 0.01em;
                    -webkit-tap-highlight-color: transparent;
                    text-shadow: 0 1px 4px rgba(0,0,0,0.4);
                }
                .kp-choice::before {
                    content: '';
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(to bottom, rgba(255,255,255,0.12) 0%, transparent 60%);
                    pointer-events: none;
                }
                .kp-choice:hover  { filter: brightness(1.15); transform: translateY(-3px) scale(1.03); }
                .kp-choice:active { transform: scale(0.94); filter: brightness(0.85); }
                /* Un hueco con palabra puesta se puede pulsar para devolverla. Se
                   nota al pasar por encima, sin gritar: la invitación es pensar
                   la respuesta, no andar quitando y poniendo. */
                .kp-hueco.quitable:hover {
                    filter: brightness(1.25);
                    box-shadow: 0 0 18px rgba(139,92,246,0.45) !important;
                }
                .kp-hueco.quitable:active { transform: scale(0.95); }

                /* Un hueco vacío que no es el de turno: se puede pulsar para
                   responderlo a él. Se insinúa al pasar por encima, no antes:
                   cuatro huecos gritando a la vez no ayudan a elegir. */
                .kp-hueco.elegible:hover {
                    border-color: rgba(6,182,212,0.6) !important;
                    color: rgba(34,211,238,0.85) !important;
                    box-shadow: 0 0 14px rgba(6,182,212,0.3) !important;
                }
                .kp-hueco.elegible:active { transform: scale(0.95); }

                @keyframes kp-confeti {
                    0%   { transform: translateY(0) rotate(var(--giro)); opacity: 0; }
                    8%   { opacity: .95; }
                    100% { transform: translateY(115vh) rotate(calc(var(--giro) + 720deg));
                           opacity: .9; }
                }

                .kp-celebra-fondo {
                    position: absolute; inset: 0; z-index: 330;
                    display: flex; align-items: center; justify-content: center;
                    padding: 18px;
                    background: rgba(4,4,10,0.82);
                    backdrop-filter: blur(16px);
                    -webkit-backdrop-filter: blur(16px);
                }
                .kp-celebra {
                    width: 100%; max-width: 440px;
                    padding: clamp(20px, 4vh, 32px) clamp(18px, 4vw, 30px);
                    border-radius: 26px;
                    border: 1px solid rgba(251,191,36,0.35);
                    background: rgba(18,16,32,0.96);
                    text-align: center;
                    font-family: 'Inter', sans-serif;
                    animation: kp-celebra-entra .45s cubic-bezier(0.34,1.56,0.64,1);
                }
                @keyframes kp-celebra-entra {
                    from { opacity: 0; transform: translateY(18px) scale(.96); }
                    to   { opacity: 1; transform: none; }
                }
                .kp-celebra-emoji { font-size: clamp(42px, 8vh, 58px); line-height: 1; }
                .kp-celebra-tit {
                    margin-top: 10px; font-size: clamp(20px, 3.4vh, 26px);
                    font-weight: 900; color: #fbbf24;
                    text-shadow: 0 0 24px rgba(251,191,36,0.45);
                }
                .kp-celebra-sub {
                    margin-top: 4px; font-size: 12px; font-weight: 700;
                    letter-spacing: .04em; color: rgba(255,255,255,0.5);
                    font-family: 'Inter', monospace;
                }
                /* Su avance, en cuatro cifras. Rob: «fiesta de ver su avance» —
                   un cartel que sólo dice «¡bien!» no premia nada. */
                .kp-celebra-cifras {
                    display: grid; grid-template-columns: repeat(4, 1fr);
                    gap: 8px; margin-top: 18px;
                }
                .kp-celebra-cifras > div {
                    padding: 10px 4px; border-radius: 14px;
                    background: rgba(255,255,255,0.04);
                    border: 1px solid rgba(255,255,255,0.07);
                }
                .kp-celebra-cifras span {
                    display: block; font-size: clamp(14px, 2.4vh, 19px);
                    font-weight: 900; color: #fff; line-height: 1.1;
                    font-family: 'Inter', sans-serif;
                }
                .kp-celebra-cifras label {
                    display: block; margin-top: 3px; font-size: 9px;
                    font-weight: 700; letter-spacing: .08em;
                    text-transform: uppercase; color: rgba(255,255,255,0.38);
                    font-family: 'Inter', monospace;
                }

                .kp-celebra-txt {
                    margin: 14px 0 0; font-size: 13px; line-height: 1.55;
                    color: rgba(226,232,240,0.78);
                }
                .kp-celebra-btns {
                    display: flex; flex-direction: column; gap: 9px; margin-top: 20px;
                }
                .kp-celebra-si, .kp-celebra-no {
                    padding: 12px 18px; border-radius: 100px;
                    font-family: 'Inter', sans-serif; font-size: 13.5px;
                    font-weight: 800; cursor: pointer; transition: all .15s;
                }
                .kp-celebra-si {
                    border: none; color: #1a1304;
                    background: linear-gradient(135deg, #fbbf24, #f59e0b);
                    box-shadow: 0 6px 24px rgba(251,191,36,0.3);
                }
                .kp-celebra-si:hover { filter: brightness(1.08); transform: translateY(-2px); }
                .kp-celebra-no {
                    border: 1px solid rgba(255,255,255,0.14);
                    background: transparent; color: rgba(226,232,240,0.7);
                }
                .kp-celebra-no:hover { background: rgba(255,255,255,0.05); color: #fff; }

                .kp-disfrute {
                    position: absolute; left: 50%; bottom: 16px;
                    transform: translateX(-50%); z-index: 180;
                    display: flex; align-items: center; gap: 12px;
                    padding: 8px 10px 8px 18px; border-radius: 100px;
                    border: 1px solid rgba(251,191,36,0.3);
                    background: rgba(18,16,32,0.9);
                    backdrop-filter: blur(10px);
                    font-family: 'Inter', sans-serif; font-size: 12.5px;
                    font-weight: 700; color: #fbbf24; white-space: nowrap;
                }
                .kp-disfrute-fin {
                    padding: 6px 14px; border-radius: 100px;
                    border: 1px solid rgba(255,255,255,0.16);
                    background: transparent; color: rgba(226,232,240,0.8);
                    font-family: 'Inter', sans-serif; font-size: 12px;
                    font-weight: 700; cursor: pointer;
                }
                .kp-disfrute-fin:hover { background: rgba(255,255,255,0.07); color: #fff; }

                .kp-choice.selected {
                    outline: 2.5px solid rgba(255,255,255,0.9);
                    outline-offset: -2.5px;
                    filter: brightness(1.2);
                }

                /* «OÍRLO OTRA VEZ». Discreto a propósito: es una salida, no una
                   invitación. Si gritara más que las opciones, el alumno lo
                   pulsaría antes de intentar oírlo. */
                .kp-repite {
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    padding: 5px 13px;
                    border-radius: 100px;
                    border: 1px dashed rgba(167,139,250,0.5);
                    background: transparent;
                    color: #c4b5fd;
                    font-family: 'Inter', sans-serif;
                    font-size: 11.5px;
                    font-weight: 700;
                    cursor: pointer;
                    white-space: nowrap;
                    transition: all .14s;
                }
                .kp-repite:hover {
                    border-style: solid;
                    background: rgba(167,139,250,0.14);
                }
                .kp-repite-nota {
                    font-size: 9px;
                    font-weight: 600;
                    opacity: .6;
                    text-transform: uppercase;
                    letter-spacing: .06em;
                }
                .kp-repite-gastado {
                    font-family: 'Inter', sans-serif;
                    font-size: 10.5px;
                    font-weight: 600;
                    color: rgba(167,139,250,0.45);
                    white-space: nowrap;
                }
            `}</style>

            {/* ── LA VUELTA DE HONOR ───────────────────────────────────────
                Rob: «cuando llega el alumno con sus 5 vidas al final de la
                canción, lanzar confeti y darle al usuario ver el vídeo completo
                de la canción... sólo si ahí lo decide el alumno».

                Se OFRECE, y las dos salidas valen lo mismo: un vídeo de siete
                minutos que arranca solo después de ganar no es un premio. */}
            <Confeti encendido={!!celebrando && !disfrutando}
                     aLoGrande={!!celebrando?.perfecto} />

            {celebrando && !disfrutando && (
                <div className="kp-celebra-fondo" data-prueba="kp-celebra">
                    <div className="kp-celebra">
                        <div className="kp-celebra-emoji">
                            {celebrando.perfecto ? '🏆' : '🎉'}
                        </div>
                        <div className="kp-celebra-tit">
                            {celebrando.perfecto ? 'Perfect run' : 'Song complete'}
                        </div>

                        {/* Su avance, que es la otra mitad de la premiación: Rob
                            pidió «fiesta de VER SU AVANCE». Un cartel que sólo dice
                            «¡bien!» no premia nada, porque no dice qué hizo. */}
                        <div className="kp-celebra-cifras">
                            <div><span>{celebrando.puntos}</span><label>Score</label></div>
                            <div>
                                <span>{celebrando.total > 0
                                    ? Math.round((celebrando.correct / celebrando.total) * 100)
                                    : 0}%</span>
                                <label>Accuracy</label>
                            </div>
                            <div>
                                <span>{celebrando.correct}/{celebrando.total}</span>
                                <label>Words</label>
                            </div>
                            <div>
                                <span>{'❤'.repeat(Math.max(0, celebrando.vidas)) || '—'}</span>
                                <label>Lives</label>
                            </div>
                        </div>

                        <p className="kp-celebra-txt">
                            {celebrando.perfecto
                                ? 'Not one mistake. Want to watch it in full? No gaps, no '
                                  + 'questions — just the video and the words you got right.'
                                : 'You made it to the end. Want to watch it in full? No gaps, '
                                  + 'no questions — just the video and your own answers.'}
                        </p>
                        <div className="kp-celebra-btns">
                            <button className="kp-celebra-si" data-prueba="kp-ver-entero"
                                    onClick={arrancaLaVuelta}>
                                ▶ Watch the whole video
                            </button>
                            <button className="kp-celebra-no" data-prueba="kp-al-marcador"
                                    onClick={vaAlMarcador}>
                                See my score →
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {disfrutando && (
                <div className="kp-disfrute" data-prueba="kp-disfrutando">
                    <span>🏆 Perfect run · enjoy it</span>
                    <button className="kp-disfrute-fin" data-prueba="kp-fin-vuelta"
                            onClick={vaAlMarcador}>
                        See my score →
                    </button>
                </div>
            )}

            <Particles trigger={partTrigger} correct={partCorrect} />
            <ScorePopup points={lastPoints} trigger={pointsTrigger} />

            {/* Screen flash on correct/wrong */}
            {effect && (
                <div style={{
                    position: 'absolute', inset: 0, zIndex: 190, pointerEvents: 'none',
                    background: effect === 'hit'
                        ? 'radial-gradient(ellipse 70% 45% at 50% 30%, rgba(52,211,153,0.18) 0%, transparent 70%)'
                        : 'radial-gradient(ellipse 70% 45% at 50% 30%, rgba(248,113,113,0.18) 0%, transparent 70%)',
                    animation: 'kp-screen-flash .8s ease forwards',
                }} />
            )}

            {/* Screen shake on wrong */}
            {effect === 'miss' && (
                <div style={{
                    position: 'absolute', inset: 0, zIndex: 195, pointerEvents: 'none',
                    animation: 'kp-shake 0.4s ease',
                }} />
            )}

            {/* ══ TOP BAR ══ */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 14px 10px 12px',
                background: 'rgba(4,4,10,0.95)',
                backdropFilter: 'blur(24px)',
                borderBottom: '1px solid rgba(255,255,255,0.04)',
                flexShrink: 0, zIndex: 10,
            }}>
                {/* Back */}
                <button onClick={onBack} style={{
                    padding: '7px 14px',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 100,
                    color: 'rgba(255,255,255,0.45)',
                    cursor: 'pointer', fontSize: 11,
                    fontFamily: "'Inter', sans-serif", fontWeight: 700,
                    whiteSpace: 'nowrap', transition: 'all .15s', flexShrink: 0,
                    letterSpacing: 0.5,
                }}>← Exit</button>

                {/* Song info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                        fontSize: 13, fontWeight: 800,
                        color: '#fff',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        lineHeight: 1.2,
                    }}>{song.title}</div>
                    <div style={{
                        fontSize: 10, color: 'rgba(255,255,255,0.35)',
                        fontFamily: "'Inter', monospace",
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>{song.artist}</div>
                </div>

                {/* Lives */}
                <LivesDisplay lives={lives} max={MAX_LIVES} />

                {/* Line counter */}
                <div style={{
                    padding: '4px 10px',
                    background: 'rgba(139,92,246,0.12)',
                    border: '1px solid rgba(139,92,246,0.25)',
                    borderRadius: 100,
                    fontSize: 10, fontWeight: 700,
                    color: '#a78bfa',
                    fontFamily: "'Inter', monospace",
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                }}>
                    {lineIdx + 1}<span style={{ opacity: 0.45 }}>/{lines.length}</span>
                </div>

                {/* Fullscreen Button */}
                {/* Solo donde el navegador puede. En el Safari del iPhone,
                    `requestFullscreen` sobre un elemento no existe —solo sobre un
                    <video>— asi que este boton no hacia NADA y ademas se tragaba el
                    error. Un boton muerto que no explica por que enseña al alumno que
                    la app no responde, y deja de fiarse de los demas botones. */}
                {hayPantallaCompleta() && (
                    <button onClick={toggleFullscreen} style={{
                        background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff',
                        padding: '6px 10px', borderRadius: '100%', cursor: 'pointer',
                        fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        ⛶
                    </button>
                )}
            </div>

            {/* ══ VIDEO — contenido sin deformarse ══ */}
            <div style={{
                flexShrink: 0,
                background: '#000',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'stretch',
                position: 'relative',
                overflow: 'hidden',
            }}>
                {/* Framing: video is centered and never wider than its 16:9 height
                    allows. El ancho vive en `.mp-video` y no aqui porque hace falta
                    una consulta por ALTO de pantalla, y un estilo en linea no se
                    puede sobreescribir desde una hoja de estilos. */}
                <div className="mp-video" style={{
                    aspectRatio: '16 / 9',
                    position: 'relative',
                    flexShrink: 0,
                }}>
                    <div id="kp2-yt-player" style={{ width: '100%', height: '100%', display: 'block' }} />
                    {/* Overlay transparente para bloquear TODAS las interacciones del usuario con YouTube */}
                    <div style={{ position: 'absolute', inset: 0, zIndex: 10, cursor: 'default' }} />
                </div>
                {/* Cinematic side vignettes — fills the black side areas */}
                <div style={{
                    position: 'absolute', inset: 0, pointerEvents: 'none',
                    background: 'linear-gradient(to right, rgba(0,0,0,0.5) 0%, transparent 15%, transparent 85%, rgba(0,0,0,0.5) 100%)',
                }} />
                {/* Bottom fade into stats dock */}
                <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0, height: 32, pointerEvents: 'none',
                    background: 'linear-gradient(to top, rgba(4,4,10,1) 0%, transparent 100%)',
                }} />
            </div>

            {/* ══ STATS DOCK ══ */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto 1fr',
                alignItems: 'center',
                padding: '10px 20px 12px',
                gap: 8,
                flexShrink: 0,
                background: 'rgba(4,4,10,0.98)',
                position: 'relative',
            }}>
                {/* Left: Flame and Play/Pause */}
                <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 15 }}>
                    <FlameStat flame={flame} />
                    <div style={{ width: 42, height: 42 }}>
                        {!isLineActive && !paused && (
                            <button 
                                onClick={togglePlayPause}
                                style={{
                                    background: 'var(--nc-accent, #8b5cf6)', 
                                    color: '#fff',
                                    border: 'none',
                                    width: 42,
                                    height: 42,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    fontSize: 18,
                                    paddingLeft: isPlaying ? 0 : 3,
                                    boxShadow: '0 0 15px rgba(139, 92, 246, 0.4)',
                                    transition: 'transform 0.2s',
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                                {isPlaying ? '⏸' : '▶'}
                            </button>
                        )}
                    </div>
                </div>

                {/* Center: Score — PROTAGONIST */}
                <ScoreDisplay score={score} scoreKey={scoreKey} />

                {/* Right: Accuracy + streak */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', letterSpacing: 1.5, fontFamily: 'monospace', textTransform: 'uppercase' }}>Accuracy</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {streak >= 2 && (
                            <div style={{
                                fontSize: 10, fontWeight: 800,
                                color: '#fbbf24',
                                fontFamily: "'Inter', monospace",
                                textShadow: '0 0 10px rgba(251,191,36,0.8)',
                                animation: 'kp-streak-in 0.4s cubic-bezier(0.34,1.56,0.64,1)',
                            }}>×{streak}</div>
                        )}
                        <div style={{
                            fontSize: 18, fontWeight: 900,
                            fontFamily: "'Inter', monospace",
                            color: pct >= 80 ? '#34d399' : pct >= 50 ? '#fbbf24' : '#f87171',
                            textShadow: `0 0 12px ${pct >= 80 ? 'rgba(52,211,153,0.6)' : pct >= 50 ? 'rgba(251,191,36,0.6)' : 'rgba(248,113,113,0.6)'}`,
                            lineHeight: 1,
                        }}>{pct}<span style={{ fontSize: 11, opacity: 0.6 }}>%</span></div>
                    </div>
                </div>
            </div>

            {/* ══ PROGRESS BAR ══ */}
            <div style={{ height: 2, background: 'rgba(255,255,255,0.04)', flexShrink: 0 }}>
                <div style={{
                    height: '100%',
                    background: 'linear-gradient(90deg, #7c3aed, #06b6d4)',
                    width: `${progress * 100}%`,
                    transition: 'width .7s cubic-bezier(0.4,0,0.2,1)',
                    boxShadow: '0 0 8px rgba(139,92,246,0.9)',
                }} />
            </div>

            {/* ══ KARAOKE AREA — 3 fixed lines, no scroll ══ */}
            <div ref={zonaRef} className="kp-karaoke" style={{
                flex: 1,
                containerType: 'size',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '16px 20px',
                paddingBottom: (currentBlank && isLineActive) ? `${altoCajon + 12}px` : '16px',
                transition: 'padding-bottom .38s cubic-bezier(0.4,0,0.2,1)',
                gap: 'clamp(4px, 2cqh, 14px)',
                overflow: 'hidden',
                position: 'relative',
            }}>

                {isLineActive ? (
                    <>
                        {/* PREVIOUS line */}
                        <div className="kp-ghost" style={{
                            // `display: none` y no dejar de renderizarla: asi el
                            // efecto que mide sigue viendo el mismo numero de
                            // hijos y no se lia contando.
                            display: sitioJusto ? 'none' : 'flex',
                            fontSize: 'clamp(10px, min(2.5vw, 4cqh), 13px)',
                            fontWeight: 400,
                            color: 'rgba(255,255,255,0.18)',
                            textAlign: 'center',
                            lineHeight: 1.6,
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                            gap: '2px 4px',
                            minHeight: 20,
                            padding: '0 8px',
                            fontFamily: "'Inter', sans-serif",
                        }}>
                            {lineIdx > 0
                                ? (lines[lineIdx - 1].words || (lines[lineIdx - 1].text || '').split(' ')).map((w, i) => (
                                    <span key={i}>{w}</span>
                                ))
                                : null}
                        </div>

                        {/* ACTIVE line */}
                        <div ref={activaRef} className="kp-activa" style={{
                            // Sin `flexShrink: 0` el navegador aplastaba esta caja
                            // hasta que "cabia", y el texto seguia saliendose por
                            // dentro. Medido daba un empate perfecto y en pantalla
                            // se veia la letra cortada: exactamente el fallo del
                            // que venimos. Esta linea es el juego; se encoge lo que
                            // esta alrededor, nunca ella.
                            flexShrink: 0,
                            // El `clamp` es solo el valor de partida del primer
                            // pintado; en cuanto `useLayoutEffect` mide, manda el
                            // numero medido. Se deja porque si por lo que sea la
                            // medicion no llegara a correr, esto sigue siendo un
                            // tamaño razonable en vez de quedarse sin ninguno.
                            fontSize: fsLetra ? `${fsLetra}px`
                                              : 'clamp(15px, min(5.5vw, 22cqh), 28px)',
                            fontWeight: 800,
                            color: '#fff',
                            textAlign: 'center',
                            // 1.6 es aire para leer; 1.32 sigue siendo legible y
                            // devuelve ~4px por fila, que en un movil corto son
                            // dos filas enteras de letra.
                            lineHeight: sitioJusto ? 1.32 : 1.6,
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '2px 4px',
                            // Menos margen a los lados = mas palabras por fila =
                            // menos filas, que es lo que de verdad ocupa alto.
                            padding: sitioJusto
                                ? 'clamp(6px, 4cqh, 10px) 12px'
                                : 'clamp(8px, 4cqh, 14px) 22px',
                            borderRadius: 22,
                            background: 'rgba(88,28,220,0.07)',
                            border: '1px solid rgba(139,92,246,0.16)',
                            width: '100%',
                            maxWidth: 640,
                            minHeight: 'clamp(40px, 15cqh, 72px)',
                            // Red de seguridad para el caso extremo: si ni a 13px
                            // cabe, se puede arrastrar dentro de su propio marco
                            // en vez de desbordar y que el centrado la parta por
                            // arriba Y por abajo a la vez, que es como se veia.
                            // Se prefiere poder llegar al texto a perderlo.
                            maxHeight: topeLetra ? `${topeLetra}px` : '100%',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            // Sin esto, `flex-wrap` reparte el alto sobrante entre
                            // las filas y la estrofa ocupaba el doble de lo que
                            // mide su texto. Era la mitad de los pixeles perdidos.
                            alignContent: 'center',
                            boxShadow: '0 0 60px rgba(88,28,220,0.08), inset 0 1px 0 rgba(255,255,255,0.03)',
                            transition: 'all .35s cubic-bezier(0.4,0,0.2,1)',
                            fontFamily: "'Inter', sans-serif",
                            animation: 'kp-line-in 0.3s ease',
                        }}>
                            {renderLine(lines[lineIdx] || {}, true, disfrutando)}
                        </div>

                        {/* NEXT line */}
                        <div className="kp-ghost" style={{
                            // `display: none` y no dejar de renderizarla: asi el
                            // efecto que mide sigue viendo el mismo numero de
                            // hijos y no se lia contando.
                            display: sitioJusto ? 'none' : 'flex',
                            fontSize: 'clamp(10px, min(2.5vw, 4cqh), 13px)',
                            fontWeight: 400,
                            color: 'rgba(255,255,255,0.15)',
                            textAlign: 'center',
                            lineHeight: 1.6,
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                            gap: '2px 4px',
                            minHeight: 20,
                            maxWidth: 560,
                            padding: '0 8px',
                            fontFamily: "'Inter', sans-serif",
                        }}>
                            {lineIdx < lines.length - 1
                                ? (lines[lineIdx + 1].words || (lines[lineIdx + 1].text || '').split(' ')).map((w, i) => (
                                    <span key={i}>{w}</span>
                                ))
                                : null}
                        </div>
                    </>
                ) : (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 20,
                        opacity: 0.8,
                        animation: 'kp-line-in 0.8s ease',
                    }}>
                        <div style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: '#a78bfa',
                            fontFamily: "'Inter', sans-serif",
                            letterSpacing: 3,
                            textTransform: 'uppercase',
                        }}>🔥</div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <div className="kp-bounce-dot" style={{ animationDelay: '0s' }} />
                            <div className="kp-bounce-dot" style={{ animationDelay: '0.2s' }} />
                            <div className="kp-bounce-dot" style={{ animationDelay: '0.4s' }} />
                        </div>
                    </div>
                )}
            </div>

            {/* ══ CHOICE DRAWER — slides from bottom ══ */}
            {currentBlank && isLineActive && (
                <div ref={cajonRef} style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 60,
                    background: 'linear-gradient(to top, rgba(4,4,10,1) 0%, rgba(4,4,10,0.97) 75%, transparent 100%)',
                    backdropFilter: 'blur(28px)',
                    // Los suelos bajan (14→10, 12→8, 8→6) solo para las pantallas
                    // cortas: cada pixel que se queda aqui es un pixel que le falta
                    // a la letra, y el cajon ya se llevaba el 40% del reproductor.
                    padding: 'clamp(10px, 2vh, 24px) 16px clamp(8px, 2vh, 18px)',
                    paddingBottom: 'calc(clamp(8px, 2vh, 18px) + env(safe-area-inset-bottom, 0px))',
                }}>
                    {/* Drawer header */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 14,
                        justifyContent: 'center', marginBottom: 'clamp(6px, 1.5vh, 14px)',
                    }}>
                        <div>
                            <div style={{
                                fontSize: 14, fontWeight: 800, color: '#e2e8f0',
                                fontFamily: "'inter', sans-serif", lineHeight: 1.2,
                            }}>
                                {blanks.length > 1
                                    ? `Blank ${currentBlank.idx + 1} of ${blanks.length}`
                                    : 'Which word goes here?'}
                            </div>
                        </div>
                        {/* ── «OÍRLO OTRA VEZ» ──────────────────────────────
                            Rob, 14-10-2026: «mi vista se fue a el oído, la letra
                            y las opciones». Tres cosas a la vez en dos segundos
                            de canción. Va aquí, al lado del reloj y encima de las
                            opciones, porque es donde está mirando justo cuando se
                            da cuenta de que no lo ha oído. */}
                        {(puedeRepetir || repetidos.has(lineIdx)) && (
                            repetidos.has(lineIdx) ? (
                                <span className="kp-repite-gastado" data-prueba="kp-repite-gastado">
                                    Heard again · your turn
                                </span>
                            ) : (
                                <button
                                    className="kp-repite"
                                    data-prueba="kp-repite"
                                    onClick={repiteElVerso}
                                    title="Plays this line again, from the start"
                                >
                                    🔁 Hear it again
                                    <span className="kp-repite-nota">once</span>
                                </button>
                            )
                        )}
                        {streak >= 2 && (
                            <div style={{
                                padding: '4px 12px', borderRadius: 100,
                                background: 'rgba(251,191,36,0.1)',
                                border: '1px solid rgba(251,191,36,0.3)',
                                fontSize: 11, fontWeight: 800,
                                color: '#fbbf24',
                                textShadow: '0 0 10px rgba(251,191,36,0.6)',
                                fontFamily: "'Inter', monospace",
                                animation: 'kp-streak-in 0.4s cubic-bezier(0.34,1.56,0.64,1)',
                            }}>🔥 ×{streak}</div>
                        )}
                    </div>

                    {/* 2×2 buttons */}
                    <div style={{
                        display: 'grid', gridTemplateColumns: '1fr 1fr',
                        gap: 10, maxWidth: 520, margin: '0 auto',
                    }}>
                        {lineWordBank.map((choiceObj, ci) => {
                            const isUsed = Object.values(answers).some(a => a?.id === choiceObj.id);
                            const pal = CHOICE_PALETTE[ci % 4];
                            return (
                                <button
                                    key={choiceObj.id}
                                    className={`kp-choice${isUsed ? ' used' : ''}`}
                                    onClick={() => {
                                        if (!isUsed) handleChoose(choiceObj);
                                    }}
                                    style={{
                                        background: pal.bg,
                                        opacity: isUsed ? 0.35 : 1,
                                        transform: isUsed ? 'scale(0.95)' : 'scale(1)',
                                        pointerEvents: isUsed ? 'none' : 'auto',
                                        boxShadow: isUsed
                                            ? 'none'
                                            : `0 6px 20px ${pal.shadow}, inset 0 1px 0 rgba(255,255,255,0.15)`,
                                        transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
                                    }}
                                >{choiceObj.word}</button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ══ FEEDBACK BADGE ══ */}
            {revealed && (
                <div style={{
                    position: 'absolute', bottom: 56, left: '50%', zIndex: 70, pointerEvents: 'none',
                    transform: 'translateX(-50%)',
                    padding: '13px 36px', borderRadius: 100,
                    fontSize: 'clamp(16px,4vw,22px)', fontWeight: 900,
                    fontFamily: "'Inter', sans-serif",
                    letterSpacing: 0.3,
                    color: allOkRevealed ? '#34d399' : '#f87171',
                    background: allOkRevealed
                        ? 'linear-gradient(135deg, rgba(16,185,129,0.15), rgba(52,211,153,0.08))'
                        : 'linear-gradient(135deg, rgba(239,68,68,0.15), rgba(248,113,113,0.08))',
                    border: `1.5px solid ${allOkRevealed ? 'rgba(52,211,153,0.4)' : 'rgba(248,113,113,0.4)'}`,
                    boxShadow: `0 0 32px ${allOkRevealed ? 'rgba(52,211,153,0.3)' : 'rgba(248,113,113,0.28)'}`,
                    whiteSpace: 'nowrap',
                    backdropFilter: 'blur(16px)',
                    animation: 'kp-feedback-in 0.45s cubic-bezier(0.34,1.56,0.64,1) forwards',
                }}>
                    {allOkRevealed ? '🎉 Perfect!' : '❌ So close!'}
                </div>
            )}

            {/* ══ BOTTOM STATUS BAR ══ */}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '6px 16px',
                background: 'rgba(4,4,10,0.98)',
                borderTop: '1px solid rgba(255,255,255,0.03)',
                fontSize: 10, color: 'rgba(255,255,255,0.22)',
                fontFamily: "'Inter', monospace",
                flexShrink: 0, zIndex: 10, gap: 12,
            }}>
                <span style={{ color: correctCount > 0 ? 'rgba(52,211,153,0.6)' : undefined }}>
                    ✓ {correctCount}/{totalCount}
                </span>
                <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.04)' }} />
                <span>
                    Line {lineIdx + 1}/{lines.length}
                </span>
            </div>
        </div>
    );
}